import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../../layout/Layout";
import PageBanner from "../../layout/PageBanner";
import { authService, cartService, catalogService, paymentService, ORDER_TYPE } from "../../services";
import { Empty, Panel, PanelHead, PayLogo, Qty, Skeleton, money } from "../../components/ui";

// ข้อมูลต่อชนิด ต้องตรงกับ CONFIG ใน TopupDetail
const KIND = {
  game: { label: "เติมเกม", orderType: ORDER_TYPE.game, historyPath: "/profile/topup-game-history", fetch: (id) => catalogService.game(id) },
  mobile: {
    label: "เติมเงินมือถือ",
    orderType: ORDER_TYPE.mobile,
    historyPath: "/profile/topup-mobile-history",
    fetch: (id) => catalogService.mobileOperator(id),
  },
  card: { label: "บัตรเติมเงิน", orderType: ORDER_TYPE.card, historyPath: "/profile/topup-card-history", fetch: (id) => catalogService.card(id) },
};

export default function CartPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(cartService.list());
  const [catalog, setCatalog] = useState({});
  const [loading, setLoading] = useState(true);
  const [channels, setChannels] = useState([]);
  const [payment, setPayment] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const sync = () => setRows(cartService.list());
    window.addEventListener("lt-cart-change", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("lt-cart-change", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  useEffect(() => {
    let alive = true;
    catalogService.orderPaymentChannels().then((data) => {
      if (!alive) return;
      setChannels(data || []);
      setPayment(data?.[0]?.id || null);
    });
    return () => {
      alive = false;
    };
  }, []);

  // โหลดข้อมูลสินค้าล่าสุดของทุกรายการในตะกร้า (ราคาอ้างอิงจากแคตตาล็อก ไม่ใช่ที่เก็บไว้)
  const refKeys = useMemo(
    () => Array.from(new Set(rows.map((row) => `${row.kind}:${row.itemId}`))).join(","),
    [rows]
  );
  useEffect(() => {
    let alive = true;
    const refs = refKeys ? refKeys.split(",") : [];
    Promise.all(
      refs.map((ref) => {
        const [kind, itemId] = ref.split(":");
        return KIND[kind] ? KIND[kind].fetch(itemId).catch(() => null) : Promise.resolve(null);
      })
    ).then((items) => {
      if (!alive) return;
      const next = {};
      refs.forEach((ref, index) => {
        next[ref] = items[index];
      });
      setCatalog(next);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [refKeys]);

  // รวมข้อมูลตะกร้ากับแคตตาล็อก
  const lines = useMemo(
    () =>
      rows.map((row) => {
        const item = catalog[`${row.kind}:${row.itemId}`] || null;
        const pack = item?.packages?.find((p) => String(p.id) === String(row.packageId)) || null;
        return { ...row, item, pack, valid: !!(item && pack), lineTotal: pack ? pack.price * row.quantity : 0 };
      }),
    [rows, catalog]
  );
  const validLines = lines.filter((line) => line.valid);
  const hasInvalid = lines.length > validLines.length;

  const channel = channels.find((c) => c.id === payment);
  const subtotal = validLines.reduce((sum, line) => sum + line.lineTotal, 0);
  const fee = channel ? Math.round(subtotal * channel.feeRate * 100) / 100 : 0;
  const total = Math.max(0, subtotal + fee);

  const canSubmit = !loading && validLines.length > 0 && !hasInvalid && accepted && payment;

  const submit = useCallback(async () => {
    if (!authService.isLoggedIn()) {
      navigate("/login", { state: { from: window.location.pathname } });
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      // กระจายค่าธรรมเนียมลงแต่ละบรรทัด ให้ผลรวมเท่ากับ fee ทั้งออเดอร์พอดี
      let feeLeft = fee;
      const items = validLines.map((line, index) => {
        const isLast = index === validLines.length - 1;
        const lineFee = isLast ? Math.round(feeLeft * 100) / 100 : Math.round(line.lineTotal * (channel?.feeRate || 0) * 100) / 100;
        feeLeft -= lineFee;
        const config = KIND[line.kind];
        return {
          productName: `${line.item.name} - ${line.pack.name}`,
          productCode: String(line.pack.id),
          productImage: line.item.image,
          quantity: line.quantity,
          price: line.pack.price,
          orderType: config.orderType,
          detail: {
            kind: line.kind,
            itemId: line.item.id,
            itemName: line.item.name,
            packageId: line.pack.id,
            packageName: line.pack.name,
            inputs: line.inputs,
            fee: lineFee,
            fromCart: true,
          },
        };
      });

      const result = await paymentService.createQrOrder({
        amount: total,
        orderType: KIND[validLines[0].kind].orderType,
        remark: `ตะกร้า ${validLines.length} รายการ`,
        items,
      });

      cartService.clear();
      navigate(`/payment/qr/${result.transactionId}`, {
        state: {
          base64: result.base64,
          amount: result.amount,
          refNo: result.refNo,
          expireDatetime: result.expireDatetime,
          title: `ตะกร้าสินค้า ${validLines.length} รายการ`,
          detail: validLines.map((line) => `${line.item.name} ${line.pack.name} x ${line.quantity}`).join(", "),
          historyPath: KIND[validLines[0].kind].historyPath,
        },
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }, [validLines, fee, total, channel, navigate]);

  const body = () => {
    if (rows.length === 0) {
      return (
        <Panel style={{ textAlign: "center" }}>
          <Empty text="ตะกร้าของคุณยังว่างอยู่" />
          <div className="lt-flex lt-gap-12 lt-mt-24" style={{ justifyContent: "center", flexWrap: "wrap" }}>
            <Link to="/topup/game" className="lt-btn lt-btn--primary">เติมเกม</Link>
            <Link to="/topup/mobile" className="lt-btn lt-btn--ghost">เติมเงินมือถือ</Link>
            <Link to="/topup/card" className="lt-btn lt-btn--ghost">บัตรเติมเงิน</Link>
          </div>
        </Panel>
      );
    }
    if (loading) return <Skeleton height={320} />;

    return (
      <>
        <Panel>
          <PanelHead step="1" title={`รายการในตะกร้า (${rows.length})`} />
          {lines.map((line) => (
            <div className="lt-cart-line" key={line.key}>
              <img className="lt-cart-line__image" src={line.item?.image || ""} alt={line.item?.name || ""} />
              <div>
                <div className="lt-cart-line__name">
                  {line.valid ? `${line.item.name} - ${line.pack.name}` : "รายการนี้ไม่มีจำหน่ายแล้ว"}
                </div>
                <div className="lt-cart-line__meta">
                  {KIND[line.kind]?.label}
                  {Object.keys(line.inputs || {}).map((key) => {
                    const field = line.item?.inputs?.find((f) => f.key === key);
                    return ` · ${field?.label || key}: ${line.inputs[key]}`;
                  })}
                </div>
                {line.valid ? <div className="lt-cart-line__meta">฿ {money(line.pack.price)} / ชิ้น</div> : null}
              </div>
              <div className="lt-cart-line__side">
                <b>฿ {money(line.lineTotal)}</b>
                {line.valid ? <Qty value={line.quantity} onChange={(value) => cartService.setQuantity(line.key, value)} /> : null}
                <button type="button" className="lt-cart-line__remove" onClick={() => cartService.remove(line.key)}>
                  ลบ
                </button>
              </div>
            </div>
          ))}
          {hasInvalid ? <p className="lt-helper lt-helper--danger">กรุณาลบรายการที่ไม่มีจำหน่ายออกก่อนชำระเงิน</p> : null}
        </Panel>

        <Panel>
          <PanelHead step="2" title="ช่องทางชำระเงิน" />
          <div className="lt-paymethod-grid">
            {channels.map((option) => (
              <button
                type="button"
                key={option.id}
                className={`lt-paymethod ${payment === option.id ? "is-active" : ""}`}
                onClick={() => setPayment(option.id)}
              >
                <span className="lt-paymethod__check">✓</span>
                <PayLogo text={option.logoText} sub={option.logoSub} />
                <div className="lt-paymethod__body">
                  <div className="lt-paymethod__name">{option.name}</div>
                  <span className={`lt-tag lt-tag--${option.tone}`}>{option.badge}</span>
                </div>
              </button>
            ))}
          </div>

          <div className="lt-summary">
            <div className="lt-summary__row">
              <span>ยอดรวม</span>
              <span>฿ {money(subtotal)}</span>
            </div>
            <div className="lt-summary__row">
              <span>ค่าธรรมเนียม</span>
              <span>฿ {money(fee)}</span>
            </div>
            <div className="lt-summary__row lt-summary__row--total">
              <span>ราคาสุทธิ</span>
              <span>฿ {money(total)}</span>
            </div>
          </div>

          <label className="lt-check lt-mt-24">
            <input type="checkbox" checked={accepted} onChange={(event) => setAccepted(event.target.checked)} />
            <span>
              ฉันยืนยันทำรายการ ยอมรับ <Link to="/policy">ข้อกำหนด</Link> และรับทราบว่าสินค้าไม่สามารถคืนเงินได้ทุกกรณี
            </span>
          </label>

          {error ? <p className="lt-helper lt-helper--danger lt-mt-24">{error}</p> : null}

          <button
            type="button"
            className={`lt-btn lt-btn--teal lt-btn--block lt-btn--cut lt-mt-24 ${canSubmit ? "" : "is-disabled"}`}
            disabled={!canSubmit || submitting}
            onClick={submit}
          >
            {submitting ? "กำลังสร้าง QR..." : "ชำระเงินทั้งหมด"}
          </button>
        </Panel>
      </>
    );
  };

  return (
    <Layout>
      <PageBanner title="ตะกร้าสินค้า" breadcrumb={[{ label: "ตะกร้าสินค้า" }]} seed={7} />
      <div className="lucid-container" style={{ marginTop: -30, position: "relative", zIndex: 3, paddingBottom: 40 }}>
        {body()}
      </div>
    </Layout>
  );
}
